import { useState } from 'react'
import { USE_EMULATOR, isConfigured } from '../firebase'
import { AuthDialog } from './AuthDialog'
import { DOWNLOADS, FIRST_RUN_NOTE } from '../lib/downloads'

/** Set these once the apps are published; falsy renders a "coming soon" chip. */
const APP_STORE_URL = ''

function Mark({ size = 28 }: { size?: number }) {
  return (
    <span className="lp-mark">
      <img src="./favicon.png" alt="" width={size} height={size} />
      <span>Indigo Notes</span>
    </span>
  )
}

/* ---------- little product vignettes, built from the app's own styles ------ */

function NoteListMock() {
  return (
    <div className="mock mock-list">
      <div className="mock-search"><span /></div>
      <div className="mock-sec">Today</div>
      <div className="mock-row sel"><b>Mortgage questions</b><i>9:41 AM · Closing costs, bridge loan…</i></div>
      <div className="mock-row"><b>Q3 planning</b><i>Yesterday · Ship the sync layer…</i></div>
      <div className="mock-sec">Previous 7 Days</div>
      <div className="mock-row"><b>Carbonara</b><i>Saturday · Guanciale, pecorino…</i></div>
    </div>
  )
}

function MathMock() {
  return (
    <div className="mock mock-paper">
      <h4>Renovation budget</h4>
      <p>Cabinets <span>4,200</span></p>
      <p>Labour <span>3,150</span></p>
      <p className="mock-sum">4,200 + 3,150 = <em>7350</em></p>
      <p className="mock-sum">7350 * 13% = <em>955.5</em></p>
    </div>
  )
}

function ChecklistMock() {
  return (
    <div className="mock mock-paper">
      <h4>To&nbsp;Do</h4>
      <p className="mock-todo"><i className="dot prog" />Draft the migration plan<span className="chip">In Progress</span></p>
      <p className="mock-todo"><i className="dot" />Book the movers</p>
      <p className="mock-todo"><i className="dot" />Call the bank</p>
      <div className="mock-divider" />
      <p className="mock-todo done"><i className="dot done" />Send bank statements</p>
    </div>
  )
}

function TagsMock() {
  return (
    <div className="mock mock-paper">
      <h4>Cupertino trip</h4>
      <p>Ferry times, cabin booking, and the rental pickup code.</p>
      <p className="mock-tags"><span>#travel</span><span>#2026</span><span>#family</span></p>
      <div className="mock-folders">
        <span className="on">Work</span><span>Home</span><span>Recipes</span>
      </div>
    </div>
  )
}

const FEATURES = [
  { eyebrow: 'Search', title: 'Find the line you half-remember.',
    body: 'Full-text search across every note — not just titles and previews. Years of notes, one keystroke away.',
    visual: <NoteListMock /> },
  { eyebrow: 'Math Notes', title: 'Type a sum, get an answer.',
    body: 'Finish any calculation with “=” and the result appears, styled so you always know what the app worked out and what you wrote.',
    visual: <MathMock /> },
  { eyebrow: 'To-dos', title: 'Not started, in progress, done.',
    body: 'A pinned to-do list with three states, plus checklists inside any note. Completed items drop quietly to the bottom.',
    visual: <ChecklistMock /> },
  { eyebrow: 'Organise', title: 'Folders when you want them, tags when you don’t.',
    body: 'Drop notes into folders, or just write #travel as you type and every tag becomes a view of its own.',
    visual: <TagsMock /> },
]

const PROOF = [
  ['Works offline', 'Write on a plane. It syncs when you land.'],
  ['Instant sync', 'Every device, in under a second.'],
  ['Yours alone', 'Notes are readable only by your account.'],
]

export function Landing() {
  const [authMode, setAuthMode] = useState<'signin' | 'signup' | null>(null)
  const go = () => setAuthMode('signup')
  const goSignIn = () => setAuthMode('signin')

  const cta = (label: string, className = 'lp-btn primary') => (
    <button className={className} onClick={go} disabled={!isConfigured}>{label}</button>
  )

  return (
    <div className="lp">
      <div className="lp-glow" aria-hidden />

      <header className="lp-nav">
        <Mark />
        <nav className="lp-nav-links">
          <a href="#features">Features</a>
          <a href="#apps">Apps</a>
        </nav>
        <button className="lp-btn ghost" onClick={goSignIn} disabled={!isConfigured}>Sign in</button>
        <button className="lp-btn primary sm" onClick={go} disabled={!isConfigured}>Sign up</button>
      </header>

      <section className="lp-hero">
        <span className="lp-eyebrow">Mac · Web · Android</span>
        <h1>Everything you<br />meant to remember.</h1>
        <p className="lp-sub">
          A fast, familiar notes app that syncs the moment you type and keeps working
          when the signal doesn’t. No subscription, no lock-in.
        </p>
        <div className="lp-cta">
          {cta('Create a free account')}
          <a className="lp-btn ghost" href="#features">See what it does</a>
        </div>
        {!isConfigured && <p className="lp-error">This deployment is missing its Firebase configuration.</p>}
        {USE_EMULATOR && <p className="lp-note">Running against the local Firebase emulator.</p>}

        <div className="lp-shot" aria-hidden>
          <div className="lp-shot-bar"><i /><i /><i /><span>Indigo Notes</span></div>
          <div className="lp-shot-body">
            <div className="lp-shot-side">
              <span className="lp-chip wide" /><span className="lp-chip" />
              <span className="lp-chip" /><span className="lp-chip short" />
              <span className="lp-chip tiny" /><span className="lp-chip short" />
            </div>
            <div className="lp-shot-list">
              <div className="lp-shot-row sel"><b /><i /></div>
              <div className="lp-shot-row"><b /><i /></div>
              <div className="lp-shot-row"><b /><i /></div>
              <div className="lp-shot-row"><b /><i /></div>
            </div>
            <div className="lp-shot-note">
              <h4 /><p /><p className="w80" /><p className="w60" />
              <p className="w40 accent" /><p className="w70" />
            </div>
          </div>
        </div>
      </section>

      <section className="lp-proof">
        {PROOF.map(([title, body]) => (
          <div key={title}><strong>{title}</strong><span>{body}</span></div>
        ))}
      </section>

      <section id="features" className="lp-rows">
        {FEATURES.map((f, i) => (
          <article key={f.title} className={`lp-row ${i % 2 ? 'flip' : ''}`}>
            <div className="lp-row-copy">
              <span className="lp-eyebrow small">{f.eyebrow}</span>
              <h2>{f.title}</h2>
              <p>{f.body}</p>
            </div>
            <div className="lp-row-visual">{f.visual}</div>
          </article>
        ))}
      </section>

      <section id="apps" className="lp-platforms">
        <h2>Wherever you think best.</h2>
        <p className="lp-sub">Open it in the browser right now, or install it on your devices.</p>
        <div className="lp-stores">
          {([
            ...DOWNLOADS.map((d) => [d.platform, d.label, d.url, d.note] as const),
            ['iOS', 'Download on the App Store', APP_STORE_URL, ''],
          ] as const).map(([kind, name, url, note]) => (
            <a key={kind} className={`lp-store ${url ? '' : 'soon'}`}
               href={url || undefined} download={url ? '' : undefined}>
              <span className="lp-store-kind">{kind}</span>
              <span className="lp-store-name">{name}</span>
              {note && url && <span className="lp-store-note">{note}</span>}
              {!url && <span className="lp-soon">Coming soon</span>}
            </a>
          ))}
        </div>
        <p className="lp-install-note">{FIRST_RUN_NOTE}</p>
        <div className="lp-cta">{cta('Create a free account')}</div>
      </section>

      {authMode && <AuthDialog initialMode={authMode} onClose={() => setAuthMode(null)} />}

      <footer className="lp-footer">
        <Mark size={22} />
        <span>© {new Date().getFullYear()} Indigo Notes</span>
      </footer>
    </div>
  )
}
